import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Building2, Mail, Phone, MapPin, Globe,
  Users, CreditCard, Shield, Key, Save, Loader2, Trophy,
} from 'lucide-react';
import { AdminConsoleLayout } from '../components/AdminConsoleLayout';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { ModuleLoadingState } from '../../shared/components/ModulePageHeader';
import { ErrorState } from '../../shared/components/ErrorState';
import { EnterpriseStatusBadge } from '../../shared/components/EnterpriseStatusBadge';
import { AdminConsoleService } from '../../../services/adminConsoleService';
import { useAuth } from '../../../context/AuthContext';
import { joinOrganization } from '../../../services/organizationService';
import { normalizeOrganizationStatus, type NormalizedOrganizationStatus } from '../../org/utils/normalizeOrganizationStatus';
import type { OrganizationDetail, ClientFeatureKey, LicenseStatus } from '../../../types/adminConsole';
import { GamificationAdminPanel } from '../../../components/GamificationAdminPanel';

// ─── helpers ────────────────────────────────────────────────────────────────

const getStatusLabel = (status: NormalizedOrganizationStatus): string => {
  if (status === 'TRIAL') return 'TRIAL';
  if (status === 'ACTIVE') return 'ATIVO';
  if (status === 'CANCELED' || status === 'SUSPENDED') return 'INATIVO';
  return 'NÃO CONFIGURADO';
};

const getStatusTone = (status: NormalizedOrganizationStatus) => {
  if (status === 'TRIAL') return 'warning' as const;
  if (status === 'ACTIVE') return 'active' as const;
  if (status === 'CANCELED' || status === 'SUSPENDED') return 'risk' as const;
  return 'draft' as const;
};

const LICENSE_STATUS_OPTIONS: { value: LicenseStatus; label: string }[] = [
  { value: 'active', label: 'Ativo' },
  { value: 'trial', label: 'Trial' },
  { value: 'suspended', label: 'Suspenso' },
  { value: 'expired', label: 'Expirado' },
];

type TabId = 'geral' | 'equipe' | 'gamificacao' | 'chaves';

const TABS: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'geral',       label: 'Geral',              icon: Building2 },
  { id: 'equipe',      label: 'Equipe e Setores',   icon: Users     },
  { id: 'gamificacao', label: 'Gamificação',         icon: Trophy    },
  { id: 'chaves',      label: 'Chaves de Recursos', icon: Key       },
];

// ─── component ──────────────────────────────────────────────────────────────

export const AdminClientDetailPage: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, refreshMemberships } = useAuth();
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<TabId>('geral');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editData, setEditData] = useState<Partial<OrganizationDetail>>({});

  const detailQuery = useQuery({
    queryKey: ['admin-client-detail', orgId],
    queryFn: () => AdminConsoleService.getOrganizationDetail(orgId!),
    enabled: !!orgId,
  });

  const usersQuery = useQuery({
    queryKey: ['admin-client-users', orgId],
    queryFn: async () => {
      const { supabase } = await import('../../../lib/supabaseClient');
      const { data: members, error: membersError } = await supabase
        .from('user_organizations')
        .select('user_id, role, created_at')
        .eq('organization_id', orgId!)
        .order('created_at', { ascending: false });
      if (membersError) {
        console.error('[AdminClientDetail] user_organizations query error:', membersError);
        throw membersError;
      }
      if (!members?.length) return [];
      const userIds = members.map((m: any) => m.user_id);
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('user_id, name, full_name, email')
        .in('user_id', userIds);
      if (profilesError) {
        console.warn('[AdminClientDetail] user_profiles query error:', profilesError);
      }
      const profileMap = Object.fromEntries((profiles || []).map((p: any) => [p.user_id, p]));
      return members.map((row: any) => ({
        id: row.user_id,
        user_id: row.user_id,
        role: row.role,
        full_name: profileMap[row.user_id]?.full_name || profileMap[row.user_id]?.name || 'Sem nome',
        email: profileMap[row.user_id]?.email || '',
        created_at: row.created_at,
      }));
    },
    enabled: !!orgId,
  });

  const featureKeysQuery = useQuery({
    queryKey: ['admin-client-features', orgId],
    queryFn: () => AdminConsoleService.getFeatureKeys(orgId!),
    enabled: !!orgId,
  });

  const departmentsQuery = useQuery({
    queryKey: ['admin-client-departments', orgId],
    queryFn: () => AdminConsoleService.getDepartments(orgId!),
    enabled: !!orgId,
  });

  const org = detailQuery.data;
  const alreadyMember = Boolean(user?.id && usersQuery.data?.some((member) => member.user_id === user.id));

  const joinMyself = async () => {
    if (!orgId || !user?.id) return;
    setJoining(true);
    setJoinError(null);
    try {
      await joinOrganization(orgId, user.id);
      await refreshMemberships();
      await queryClient.invalidateQueries({ queryKey: ['admin-client-users', orgId] });
    } catch (err) {
      setJoinError(err instanceof Error ? err.message : 'Não foi possível entrar na organização.');
    } finally {
      setJoining(false);
    }
  };

  const startEditing = () => {
    if (!org) return;
    setEditData({
      name: org.name,
      cnpj: org.cnpj,
      address: org.address,
      timezone: org.timezone,
      industry: org.industry,
      contact_email: org.contact_email,
      phone: org.phone,
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!orgId) return;
    setIsSaving(true);
    try {
      await AdminConsoleService.updateOrganization(orgId, editData);
      await queryClient.invalidateQueries({ queryKey: ['admin-client-detail', orgId] });
      await queryClient.invalidateQueries({ queryKey: ['admin-clients'] });
      setIsEditing(false);
    } catch (err) {
      console.error('Erro ao salvar:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleStatusChange = async (newStatus: LicenseStatus) => {
    if (!org) return;
    try {
      const { supabase } = await import('../../../lib/supabaseClient');
      const { data } = await supabase
        .from('client_licenses')
        .select('id')
        .eq('organization_id', orgId!)
        .maybeSingle();
      if (data?.id) {
        await AdminConsoleService.updateLicenseStatus(data.id, newStatus);
        await queryClient.invalidateQueries({ queryKey: ['admin-client-detail', orgId] });
        await queryClient.invalidateQueries({ queryKey: ['admin-clients'] });
      }
    } catch (err) {
      console.error('Erro ao alterar status:', err);
    }
  };

  const handleTabChange = (tab: TabId) => {
    if (isEditing) {
      setIsEditing(false);
    }
    setActiveTab(tab);
  };

  return (
    <RequireCapability capability="admin.access" realRoleOnly>
      <AdminConsoleLayout>
        <div className="enterprise-page h-full flex flex-col overflow-hidden">

          {/* ── Page header ──────────────────────────────────── */}
          <div className="flex items-center gap-3 px-5 pt-4 pb-3 shrink-0">
            <button
              type="button"
              onClick={() => navigate('/administracao/clientes')}
              className="enterprise-btn-ghost p-1.5"
            >
              <ArrowLeft size={16} />
            </button>
            <Building2 size={18} className="text-[var(--text-muted)]" />
            <div className="flex-1 min-w-0">
              <h1 className="text-[14px] font-semibold truncate">
                {detailQuery.isPending ? 'Carregando...' : org?.name || 'Workspace'}
              </h1>
              <p className="text-[11px] text-[var(--text-muted)]">Instância do cliente na plataforma SaaS</p>
            </div>

            {/* Editar / Salvar — visível apenas na aba Geral */}
            {activeTab === 'geral' && org && !isEditing && (
              <button
                type="button"
                onClick={startEditing}
                className="enterprise-btn-primary text-[12px] px-3 py-1.5"
              >
                Editar
              </button>
            )}
            {activeTab === 'geral' && isEditing && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="enterprise-btn-ghost text-[12px] px-3 py-1.5"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="enterprise-btn-primary text-[12px] px-3 py-1.5 flex items-center gap-1.5"
                >
                  {isSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                  Salvar
                </button>
              </div>
            )}
          </div>

          {/* ── States ───────────────────────────────────────── */}
          {detailQuery.isPending && <ModuleLoadingState label="Carregando dados do workspace" />}
          {detailQuery.isError && (
            <ErrorState
              message="Não foi possível carregar os dados do workspace."
              onRetry={detailQuery.refetch}
            />
          )}

          {org && (
            <>
              {/* ── Tab bar ──────────────────────────────────── */}
              <div className="shrink-0 border-b border-[var(--border-default)] px-5">
                <nav className="flex gap-0" aria-label="Abas de detalhes">
                  {TABS.map(({ id, label, icon: Icon }) => {
                    const active = activeTab === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => handleTabChange(id)}
                        className={[
                          'relative flex items-center gap-1.5 px-3 py-2.5 text-[12px] font-medium transition-colors whitespace-nowrap',
                          active
                            ? 'text-blue-600 dark:text-blue-400'
                            : 'text-[var(--text-muted)] hover:text-[var(--text-default)]',
                        ].join(' ')}
                      >
                        <Icon size={13} strokeWidth={active ? 2.5 : 2} />
                        {label}
                        {active && (
                          <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-blue-600 dark:bg-blue-400 rounded-t-full" />
                        )}
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* ── Tab panels ───────────────────────────────── */}
              <div className="flex-1 overflow-y-auto">

                {/* ── TAB: Geral ─────────────────────────────── */}
                {activeTab === 'geral' && (
                  <div className="px-5 py-4 space-y-4">

                    {/* Informações Gerais */}
                    <div className="enterprise-card">
                      <div className="enterprise-card-header flex items-center gap-2">
                        <Building2 size={14} />
                        <span className="text-[12px] font-semibold">Informações Gerais</span>
                        {org.status && (
                          <EnterpriseStatusBadge
                            label={getStatusLabel(normalizeOrganizationStatus({ status: org.status }))}
                            tone={getStatusTone(normalizeOrganizationStatus({ status: org.status }))}
                          />
                        )}
                      </div>
                      <div className="p-4 grid grid-cols-2 gap-x-6 gap-y-3 text-[12px]">
                        <InfoRow icon={Building2} label="Nome" value={isEditing ? undefined : org.name}>
                          {isEditing && (
                            <input
                              className="enterprise-input text-[12px] w-full"
                              value={editData.name || ''}
                              onChange={(e) => setEditData(p => ({ ...p, name: e.target.value }))}
                            />
                          )}
                        </InfoRow>
                        <InfoRow icon={Globe} label="CNPJ" value={isEditing ? undefined : (org.cnpj || null)}>
                          {isEditing && (
                            <input
                              className="enterprise-input text-[12px] w-full"
                              value={editData.cnpj || ''}
                              onChange={(e) => setEditData(p => ({ ...p, cnpj: e.target.value }))}
                            />
                          )}
                        </InfoRow>
                        <InfoRow icon={Mail} label="Email" value={isEditing ? undefined : (org.contact_email || null)}>
                          {isEditing && (
                            <input
                              className="enterprise-input text-[12px] w-full"
                              value={editData.contact_email || ''}
                              onChange={(e) => setEditData(p => ({ ...p, contact_email: e.target.value }))}
                            />
                          )}
                        </InfoRow>
                        <InfoRow icon={Phone} label="Telefone" value={isEditing ? undefined : (org.phone || null)}>
                          {isEditing && (
                            <input
                              className="enterprise-input text-[12px] w-full"
                              value={editData.phone || ''}
                              onChange={(e) => setEditData(p => ({ ...p, phone: e.target.value }))}
                            />
                          )}
                        </InfoRow>
                        <InfoRow icon={MapPin} label="Endereço" value={isEditing ? undefined : (org.address || null)}>
                          {isEditing && (
                            <input
                              className="enterprise-input text-[12px] w-full"
                              value={editData.address || ''}
                              onChange={(e) => setEditData(p => ({ ...p, address: e.target.value }))}
                            />
                          )}
                        </InfoRow>
                        <InfoRow icon={Globe} label="Indústria" value={isEditing ? undefined : (org.industry || null)}>
                          {isEditing && (
                            <input
                              className="enterprise-input text-[12px] w-full"
                              value={editData.industry || ''}
                              onChange={(e) => setEditData(p => ({ ...p, industry: e.target.value }))}
                            />
                          )}
                        </InfoRow>
                        <InfoRow icon={Globe} label="Timezone" value={isEditing ? undefined : (org.timezone || null)}>
                          {isEditing && (
                            <input
                              className="enterprise-input text-[12px] w-full"
                              value={editData.timezone || ''}
                              onChange={(e) => setEditData(p => ({ ...p, timezone: e.target.value }))}
                            />
                          )}
                        </InfoRow>
                        <InfoRow
                          icon={CreditCard}
                          label="Plano"
                          value={org.plan ? String(org.plan).toUpperCase() : null}
                        />
                      </div>
                    </div>

                    {/* Licença */}
                    <div className="enterprise-card">
                      <div className="enterprise-card-header flex items-center gap-2">
                        <Shield size={14} />
                        <span className="text-[12px] font-semibold">Licença</span>
                      </div>
                      <div className="p-4 grid grid-cols-3 gap-4 text-[12px]">
                        <div>
                          <span className="text-[var(--text-muted)]">Status</span>
                          <div className="mt-1">
                            <select
                              className="enterprise-input text-[12px]"
                              value={org.status || ''}
                              onChange={(e) => handleStatusChange(e.target.value as LicenseStatus)}
                            >
                              {LICENSE_STATUS_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div>
                          <span className="text-[var(--text-muted)]">Usuários Ativos</span>
                          <p className="font-semibold mt-1">{org.active_users ?? 0} / {org.max_users ?? 0}</p>
                        </div>
                        <div>
                          <span className="text-[var(--text-muted)]">Criado em</span>
                          <p className="font-semibold mt-1">
                            {org.created_at
                              ? new Date(org.created_at).toLocaleDateString('pt-BR')
                              : <EmptyValue />}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── TAB: Equipe e Setores ──────────────────── */}
                {activeTab === 'equipe' && (
                  <div className="px-5 py-4 space-y-4">

                    {/* Usuários */}
                    <div className="enterprise-card">
                      <div className="enterprise-card-header flex items-center gap-2">
                        <Users size={14} />
                        <span className="text-[12px] font-semibold">
                          Usuários ({usersQuery.data?.length ?? 0})
                        </span>
                        {!alreadyMember && (
                          <button
                            type="button"
                            onClick={() => void joinMyself()}
                            disabled={joining || !user?.id}
                            className="ml-auto rounded bg-slate-900 px-2 py-1 text-[11px] font-semibold text-white disabled:opacity-50"
                          >
                            {joining ? 'Incluindo…' : 'Incluir meu usuário'}
                          </button>
                        )}
                      </div>
                      {joinError && <div className="px-4 pt-2 text-[12px] text-rose-500">{joinError}</div>}
                      {usersQuery.isPending ? (
                        <div className="p-4 text-[12px] text-[var(--text-muted)]">Carregando...</div>
                      ) : usersQuery.isError ? (
                        <div className="p-4 text-[12px] text-rose-500">
                          Erro ao carregar usuários: {(usersQuery.error as any)?.message || 'verifique o console para detalhes.'}
                        </div>
                      ) : usersQuery.data?.length ? (
                        <div className="enterprise-table-shell">
                          <table className="w-full text-left text-sm">
                            <thead className="enterprise-table-header">
                              <tr>
                                <th className="enterprise-table-head-cell">Nome</th>
                                <th className="enterprise-table-head-cell">Email</th>
                                <th className="enterprise-table-head-cell">Papel</th>
                                <th className="enterprise-table-head-cell">Desde</th>
                              </tr>
                            </thead>
                            <tbody>
                              {usersQuery.data.map((u: any) => (
                                <tr key={u.id} className="enterprise-table-row">
                                  <td className="enterprise-table-cell font-medium">{u.full_name}</td>
                                  <td className="enterprise-table-cell text-[var(--text-muted)]">{u.email || '—'}</td>
                                  <td className="enterprise-table-cell">
                                    <span className="inline-flex px-2 py-0.5 rounded text-[11px] font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300">
                                      {u.role}
                                    </span>
                                  </td>
                                  <td className="enterprise-table-cell text-[var(--text-muted)]">
                                    {new Date(u.created_at).toLocaleDateString('pt-BR')}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="p-4 text-[12px] text-[var(--text-muted)]">Nenhum usuário nesta organização.</div>
                      )}
                    </div>

                    {/* Departamentos */}
                    <div className="enterprise-card">
                      <div className="enterprise-card-header flex items-center gap-2">
                        <Building2 size={14} />
                        <span className="text-[12px] font-semibold">
                          Departamentos ({departmentsQuery.data?.length ?? 0})
                        </span>
                      </div>
                      {departmentsQuery.isPending ? (
                        <div className="p-4 text-[12px] text-[var(--text-muted)]">Carregando...</div>
                      ) : departmentsQuery.data?.length ? (
                        <div className="p-4 space-y-2">
                          {departmentsQuery.data.map((dept: any) => (
                            <div key={dept.id} className="text-[12px]">
                              <span className="font-medium">{dept.name}</span>
                              {dept.sectors?.length > 0 && (
                                <span className="text-[var(--text-muted)] ml-2">
                                  ({dept.sectors.length} setor{dept.sectors.length > 1 ? 'es' : ''})
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-4 text-[12px] text-[var(--text-muted)]">Nenhum departamento cadastrado.</div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── TAB: Gamificação ───────────────────────── */}
                {activeTab === 'gamificacao' && (
                  <div className="px-5 py-4">
                    <GamificationAdminPanel
                      organizationId={orgId ?? null}
                      organizationName={org.name}
                    />
                  </div>
                )}

                {/* ── TAB: Chaves de Recursos ────────────────── */}
                {activeTab === 'chaves' && (
                  <div className="px-5 py-4">
                    <div className="enterprise-card">
                      <div className="enterprise-card-header flex items-center gap-2">
                        <Key size={14} />
                        <span className="text-[12px] font-semibold">
                          Feature Keys ({featureKeysQuery.data?.length ?? 0})
                        </span>
                      </div>
                      {featureKeysQuery.isPending ? (
                        <div className="p-4 text-[12px] text-[var(--text-muted)]">Carregando...</div>
                      ) : featureKeysQuery.data?.length ? (
                        <div className="p-4">
                          <div className="flex flex-wrap gap-2">
                            {featureKeysQuery.data.map((fk: ClientFeatureKey) => (
                              <span
                                key={fk.id}
                                className={[
                                  'inline-flex items-center gap-1 px-2.5 py-1 rounded-md border text-[11px] font-medium',
                                  fk.enabled
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900/60'
                                    : 'bg-zinc-100 text-zinc-500 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:border-zinc-700',
                                ].join(' ')}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full ${fk.enabled ? 'bg-emerald-500' : 'bg-zinc-400'}`} />
                                {fk.feature_key}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="p-4 text-[12px] text-[var(--text-muted)]">Nenhuma feature key configurada.</div>
                      )}
                    </div>
                  </div>
                )}

              </div>
            </>
          )}
        </div>
      </AdminConsoleLayout>
    </RequireCapability>
  );
};

// ─── helpers ────────────────────────────────────────────────────────────────

const EmptyValue: React.FC = () => (
  <span className="italic text-[var(--text-muted)] font-normal">Não informado</span>
);

const InfoRow: React.FC<{
  icon: React.ElementType;
  label: string;
  value?: string | null;
  children?: React.ReactNode;
}> = ({ icon: Icon, label, value, children }) => {
  // When not editing and value is absent, render the row with muted italic
  const isEmpty = !children && (value === null || value === undefined || value === '');
  return (
    <div className="flex items-start gap-2">
      <Icon size={13} className="text-[var(--text-muted)] mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <span className="text-[var(--text-muted)] text-[11px]">{label}</span>
        {children
          ? <div className="mt-0.5">{children}</div>
          : isEmpty
            ? <p className="font-normal mt-0.5"><EmptyValue /></p>
            : <p className="font-medium truncate mt-0.5">{value}</p>
        }
      </div>
    </div>
  );
};
